import { Alert, Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import { useRef, useState } from "react";

import useDebouncedState from "~/shared/hooks/useDebouncedState";

import { UseAddressAutocompleteItem } from "./types";
import useAddressAutocomplete from "./useAddressAutocomplete";

type Props = {
    onSelect: (value: string) => void;
    noOptionsText: string;
    fieldLabel: string;
    initialValue?: string | null;
    disabled: boolean;
    size?: "small" | "medium";
};

export default function AddressAutocompleteField({
    onSelect,
    noOptionsText,
    fieldLabel,
    initialValue,
    disabled,
    size = "medium",
}: Props) {
    const [value, setValue] = useState<UseAddressAutocompleteItem | null>(
        !initialValue ? null : { label: initialValue, addressId: "" },
    );

    const [_, debouncedValue, setInputValue] = useDebouncedState<string>("", 300);
    const { addresses, error, isLoading } = useAddressAutocomplete(debouncedValue);
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <>
            {error && (
                <Alert severity="error">
                    <Typography>{error.message}</Typography>
                </Alert>
            )}
            <Autocomplete
                filterOptions={x => x}
                options={addresses}
                autoComplete
                includeInputInList
                filterSelectedOptions
                // once you have an address, you cannot remove it, only change it
                disableClearable={initialValue !== null}
                value={value}
                noOptionsText={isLoading ? <CircularProgress /> : noOptionsText}
                onChange={(_, newValue: UseAddressAutocompleteItem | null) => {
                    setValue(newValue);
                    onSelect(newValue?.addressId ?? "");
                }}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={params => {
                    return <TextField {...params} label={fieldLabel} fullWidth inputRef={inputRef} />;
                }}
                disabled={disabled}
                size={size}
            />
        </>
    );
}
